import React, { useEffect, useState } from 'react';
import { Smartphone } from 'lucide-react';
import { motion } from 'framer-motion';

import { getAllPhoneNumber, getPhoneNumberByOrgId } from '../utils/api/phoneNumberAPI';
import { convertToIndianTimeZone } from '../utils/utils';
import { useSelector } from 'react-redux';

function PhoneNumberLayer() {
  const [phoneNumberList, setPhoneNumberList] = useState([]);
  const [loading, setLoading] = useState(true);
  const userOrgId = useSelector(state => state?.user?.user?.organizationId);

  useEffect(() => {
    setLoading(true);
    getPhoneNumberByOrgId(userOrgId)
      .then((data) => setPhoneNumberList(data.phoneNumbers || []))
      .catch((error) => console.error('Error fetching phone numbers:', error))
      .finally(() => setLoading(false));
  }, []);

  const renderEmptyState = () => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="card h-100 p-10 radius-12"
    >
      <div
        style={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
        }}
      >
        <Smartphone size={100} color="#6b7280" strokeWidth={1.5} />
        <h1 className="text-sm pt-10 pb-10" style={{ color: '#9ca3af' }}>
          Manage Your Numbers with CallHQ
        </h1>
        <p className="text-m" style={{ color: '#9ca3af' }}>
          Seamlessly connect AI agents to real phone numbers for outbound and inbound calls.
        </p>
        <p className="text-m" style={{ color: '#9ca3af' }}>
          Link numbers from Twilio, Vonage, or create free virtual numbers directly via CallHQ.
        </p>
        <div className="d-flex mt-4" style={{ gap: '12px' }}>
          <button type="button" className="btn btn-primary-600 radius-8 px-20 py-11">
            Get a Number
          </button>
          <button type="button" className="btn btn-outline-primary-600 radius-8 px-20 py-11">
            How It Works
          </button>
        </div>
      </div>
    </motion.div>
  );

  if (loading || phoneNumberList.length === 0) {
    return renderEmptyState();
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="card h-100 p-0 radius-12"
    >
      {/* Table Section */}
      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th className="text-center">ID</th>
                <th className="text-center">Number</th>
                <th className="text-center">Created At</th>
                <th className="text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {phoneNumberList.map((phone, idx) => (
                <tr key={phone.id}>
                  <td
                    className="text-center d-flex align-items-center justify-content-center"
                    style={{  }}
                  >
                    {phone.id}
                    {idx === 0 && (
                      <div>
                        <small className="text-muted fst-italic">(Trial)</small>
                      </div>
                    )}
                  </td>
                  <td className="text-center">{phone?.did?.slice(2) || '-'}</td>
                  <td className="text-center">
                    {convertToIndianTimeZone(phone?.createdAt) || '-'}
                  </td>
                  <td className="text-center">
                    <span
                      className={`badge ${phone?.status ? 'bg-success' : 'bg-secondary'}`}
                      style={{
                        padding: '4px 10px',
                        fontSize: '12px',
                        borderRadius: '6px',
                      }}
                    >
                      {phone?.status ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="card bg-white d-flex justify-content-between align-items-center px-24 py-16 ">
        <div
          className="bg-light border px-24 py-20"
          style={{
            borderRadius: '8px',
            width: '100%',
            maxWidth: '600px',
            margin: '0 auto',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          }}
        >
          <div className="text-muted small lh-sm">
            <strong className="d-block mb-2">Want to Purchase a New Phone Number?</strong>
            To activate your dedicated phone number, <strong>KYC verification</strong> is required.
            <br />
            Please email the following documents to{' '}
            <a
              href="mailto:hello@callhq.ai"
              style={{ color: 'var(--primary-300)' }}
            >
              hello@callhq.ai
            </a>:
            <ol className="mt-2 mb-2 ps-4">
              <li>PAN Card</li>
              <li>Aadhaar Card</li>
              <li>(If registering as a business) Certificate of Incorporation (COI)</li>
            </ol>
            <strong>Activation Timeline:</strong> Your number will be activated within 7 days of
            successful verification.
            <br />
            <em>In the meantime:</em> You can continue using the CallHQ trial phone number for testing
            and calling.
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default PhoneNumberLayer;
